"use client";

import Footer from "../ui/Footer/Footer";
import { useRouter } from "@/i18n/navigation";
import { useCommerceSettings } from "@/contexts/CommerceSettingsContext";
import { useEffect, useState } from "react";

import moment from "moment-timezone";
import { toast } from "react-toastify";
import { useSearchParams } from "next/navigation";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useSession } from "next-auth/react";
import { usePostHog } from "posthog-js/react";
import { useTranslations, useLocale } from "next-intl";
import PlanSwitcher from "../Course/PlanSwitcher/PlanSwitcher";
import Image from "next/image";
import dynamic from "next/dynamic";
import { Link } from "@/i18n/navigation";
import CourseSection from "./CourseSection";
import AssignmentSection from "./AssignmentSection";
import BookedEvent from "../content/BookedEvent/BookedEvent";
import CourseCard from "@/components/CourseCard/CourseCard";
import RecommendedPath from "@/components/Dashboard/RecommendedPath";
import DailyGoalWidget from "@/components/Dashboard/DailyGoalWidget";
import { getCourseBySlug } from "@/lib/courses-catalog";
import PremiumCTA from "@/components/Premium/PremiumCTA/PremiumCTA";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import PublicLottieAnimation from "@/components/PublicLottieAnimation/PublicLottieAnimation";
import bebot from "@/public/static/animations/bebot.json";
import { articleCoverPath } from "@/helpers/articleCover";

// Below-the-fold components — code-split to keep the initial bundle slim.
// WatchStatsSection alone pulls in Recharts (~110KB gzipped).
// LeagueWidget + QuestList depend on client-only hooks (useSession, polling)
// so SSR brings no benefit; ssr:false saves an extra render pass.
const WatchStatsSection = dynamic(
    () => import("@/components/Dashboard/WatchStatsSection"),
    {
        ssr: false,
        loading: () => (
            <div className="mt-14 lg:mt-24">
                <Skeleton height={280} borderRadius={12} />
            </div>
        ),
    },
);

const LeagueWidget = dynamic(
    () => import("@/components/Dashboard/LeagueWidget"),
    {
        ssr: false,
        loading: () => (
            <div className="mt-5">
                <Skeleton height={200} borderRadius={12} />
            </div>
        ),
    },
);

const QuestList = dynamic(() => import("@/components/Quests/QuestList"), {
    ssr: false,
    loading: () => (
        <div className="mt-5">
            <Skeleton height={150} borderRadius={12} />
        </div>
    ),
});

// Reps : point d'entrée quotidien. Skeleton pendant le chargement (comme les
// autres widgets) ; le composant se masque ensuite s'il n'y a aucun contenu Reps.
const RepsWidget = dynamic(() => import("@/components/Dashboard/RepsWidget"), {
    ssr: false,
    loading: () => (
        <div className="mt-4">
            <Skeleton height={176} borderRadius={16} />
        </div>
    ),
});

/**
 * Une dernière carte seule sur sa ligne laisse deux emplacements vides à côté
 * d'elle, et la section semble cassée. La sélection d'articles est filtrée
 * (centres d'intérêt, langue, déjà lus) : son nombre tombe rarement juste.
 *
 * On masque cette carte isolée, mais uniquement au palier où elle l'est : à 4
 * articles elle disparaît sur trois colonnes et reste visible en dessous, où
 * les lignes tombent justes. Rien n'est masqué en une seule colonne.
 */
function orphanCardClass(index, count) {
    if (count <= 1 || index !== count - 1) return "";

    const aloneOnTwoColumns = count % 2 === 1;
    const aloneOnThreeColumns = count % 3 === 1;

    if (aloneOnTwoColumns && aloneOnThreeColumns) return "sm:hidden";
    if (aloneOnTwoColumns) return "sm:hidden xl:block";
    if (aloneOnThreeColumns) return "xl:hidden";

    return "";
}

/**
 * Build the ordered "allCourses" array from the raw personal payload.
 * Same logic as the legacy loadDashboard() fetch handler, isolated here
 * so it can be reused both at first paint (from SSR bootstrap props)
 * and at refresh (after archiving, etc.).
 */
function buildAllCoursesFromPersonal(personal) {
    if (!personal) return [];

    const userCourses = personal.userCourses || [];
    const userPrograms = personal.userPrograms || [];
    const subsPrograms = personal.subsPrograms || [];
    const studentAccelerators = personal.studentAccelerators || [];
    const progressions = personal.progressions || {};

    const allCourses = [];
    userCourses.forEach((c) => allCourses.push({ ...c, _kind: "buyer" }));
    userPrograms.forEach((p) => allCourses.push({ ...p, _kind: "buyer" }));
    subsPrograms.forEach((p) => allCourses.push({ ...p, _kind: "subscription" }));
    studentAccelerators.forEach((a) => allCourses.push({ ...a, _kind: "student" }));

    // Order progressions: by id (key), then re-sort dated ones by last date desc,
    // and append never-progressed ones at the end. Same shape as the legacy code.
    const orderedProgressions = [];
    Object.keys(progressions)
        .sort()
        .forEach((key) => {
            progressions[key].id = key;
            orderedProgressions[key] = progressions[key];
        });

    const dated = orderedProgressions
        .filter((p) => p && p.last !== null)
        .sort((a, b) => new Date(a.last.date) - new Date(b.last.date))
        .reverse();

    const undated = orderedProgressions.filter(
        (p) => p && p.last === null,
    );

    const ordered = [...dated, ...undated];

    const allCoursesOrdered = [];
    ordered.forEach((progression) => {
        allCourses.forEach((course) => {
            if (course.product) {
                if (course.product.id == progression.id) {
                    course.progression = progression;
                    allCoursesOrdered.push(course);
                }
            } else if (course.program) {
                if (course.program.id == progression.id) {
                    course.progression = progression;
                    allCoursesOrdered.push(course);
                }
            }
        });
    });

    return allCoursesOrdered;
}

/**
 * Merge events + calls into the single chronologically-sorted list
 * the dashboard displays under "upcoming events".
 */
function buildEventsPlanned(events, calls) {
    const upcoming = (events || []).filter(
        (event) => event.eventDate && !event.eventDate.isEnded,
    );
    const merged = [...upcoming, ...(calls || [])];
    merged.sort((a, b) => {
        return (
            moment(a.eventDate ? a.eventDate.date : a.plannedDate).unix() -
            moment(b.eventDate ? b.eventDate.date : b.plannedDate).unix()
        );
    });
    return { events: upcoming, allEventsPlanned: merged };
}

export default function Dashboard({
    articles,
    popularArticles = [],
    lastCourses,
    series,
    mentors,
    highlights: episodesToHighlight,
    recommendedCourses = [],
    recommendation = null,
    bootstrap = null,
}) {
    // Refs
    const { data: session, status } = useSession();
    const router = useRouter();
    const searchParams = useSearchParams();
    const t = useTranslations("dashboard");
    const locale = useLocale();
    const posthog = usePostHog();
    // Période d'essai d'onboarding (/admin/promotions) : la carte d'appel à
    // l'essai disparaît dès que l'essai est coupé, pour ne pas promettre ce
    // que le tunnel refusera.
    const { settings: commerceSettings } = useCommerceSettings();
    const onboardingTrial = commerceSettings.trial.onboarding;

    // Mesure : clic sur un article recommandé du dashboard
    const handleArticleClickCapture = (event) => {
        if (!posthog) return;
        const link = event.target.closest("a");
        const href = link?.getAttribute("href") || "";
        if (href.includes("/r/")) {
            const slug = href.split("/r/")[1]?.split(/[/?#]/)[0];
            posthog.capture("article_clicked", {
                slug: slug || null,
                source: "dashboard",
            });
        }
    };

    // Mesure : clic sur un article du classement « les plus lus »
    const handlePopularArticleClickCapture = (event) => {
        if (!posthog) return;
        const link = event.target.closest("a");
        const href = link?.getAttribute("href") || "";
        if (href.includes("/r/")) {
            const slug = href.split("/r/")[1]?.split(/[/?#]/)[0];
            posthog.capture("article_clicked", {
                slug: slug || null,
                source: "dashboard_popular",
            });
        }
    };

    // Pre-computed initial values from the SSR bootstrap payload
    // (a single round trip already fetched everything below)
    const initialPersonal = bootstrap?.personal || null;
    const initialBadges = bootstrap?.badges || null;
    const initialEvents = buildEventsPlanned(
        initialPersonal?.events,
        initialPersonal?.calls,
    );

    // States — seeded from bootstrap so the first paint is instant
    const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
    const [allCourses, setAllCourses] = useState(() =>
        buildAllCoursesFromPersonal(initialPersonal),
    );
    const [userCourses, setUserCourses] = useState(
        initialPersonal?.userCourses || [],
    );
    const [userPrograms, setUserPrograms] = useState(
        initialPersonal?.userPrograms || [],
    );
    const [certifications, setCertifications] = useState(
        initialPersonal?.certifications || [],
    );
    const [progressions, setProgressions] = useState(
        initialPersonal?.progressions || [],
    );
    const [tasks, setTasks] = useState(initialPersonal?.tasks || []);
    const [subsPrograms, setSubsPrograms] = useState(
        initialPersonal?.subsPrograms || [],
    );
    const [all, setAll] = useState(false);
    const [filterCategory, setFilterCategory] = useState(
        initialPersonal?.filterCategory || "all",
    );
    const [calls, setCalls] = useState(initialPersonal?.calls || []);
    const [events, setEvents] = useState(initialEvents.events);
    const [progressBarWidth, setProgressBarWidth] = useState(0);

    // New states
    const [allEventsPlanned, setAllEventsPlanned] = useState(
        initialEvents.allEventsPlanned,
    );
    const [showAllEvents, setShowAllEvents] = useState(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [modal, setModal] = useState(null);
    const [refreshVideo, setRefreshVideo] = useState(false);
    const [currentTime, setCurrentTime] = useState(moment());
    const [badgeData, setBadgeData] = useState(
        initialBadges ? { badges: initialBadges.available || [] } : null,
    );
    const [userProgress, setUserProgress] = useState(() => {
        if (!initialBadges?.progress?.length) return null;
        return initialBadges.progress[0];
    });
    const [userAssignments, setUserAssignments] = useState(
        bootstrap?.assignments || [],
    );
    const [bebotCover, setBebotCover] = useState(
        bootstrap?.discordCover || null,
    );
    const [isBebotLoading, setIsBebotLoading] = useState(
        bootstrap?.discordCover === undefined,
    );
    const [archivedCount, setArchivedCount] = useState(
        initialPersonal?.archivedCount || 0,
    );

    const loadDashboard = async () => {
        if (!isLoadingDashboard) {
            setIsLoadingDashboard(true);

            const response = await fetch("/api/user/get/dashboard", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    locale: locale,
                }),
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setIsLoadingDashboard(false);
                toast.error(fetchedData.message);
            } else {
                setUserCourses(fetchedData.userCourses);
                setUserPrograms(fetchedData.userPrograms);
                setCertifications(fetchedData.certifications);
                setProgressions(fetchedData.progressions);
                setTasks(fetchedData.tasks);
                setSubsPrograms(fetchedData.subsPrograms);
                setArchivedCount(fetchedData.archivedCount || 0);
                setFilterCategory(fetchedData.filterCategory);
                setCalls(fetchedData.calls);
                let tempEvents = [];
                fetchedData.events.forEach((event) => {
                    if (!event.eventDate.isEnded) {
                        tempEvents.push(event);
                    }
                });
                setEvents(tempEvents);
                const tempPlanned = [...tempEvents].concat(fetchedData.calls);

                // Sort dates moment
                tempPlanned.sort((a, b) => {
                    return (
                        moment(
                            a.eventDate ? a.eventDate.date : a.plannedDate,
                        ).unix() -
                        moment(
                            b.eventDate ? b.eventDate.date : b.plannedDate,
                        ).unix()
                    );
                });
                setAllEventsPlanned(tempPlanned);
                // Take userCourses and userPrograms and subsPrograms and put them in one array
                let allCourses = [];
                fetchedData.userCourses.forEach((course) => {
                    allCourses.push({ ...course, _kind: "buyer" });
                });
                fetchedData.userPrograms.forEach((program) => {
                    allCourses.push({ ...program, _kind: "buyer" });
                });
                fetchedData.subsPrograms.forEach((program) => {
                    allCourses.push({ ...program, _kind: "subscription" });
                });
                fetchedData.studentAccelerators.forEach((accelerator) => {
                    allCourses.push({ ...accelerator, _kind: "student" });
                });

                // Order object dictionary fetchedData.progressions
                let orderedProgressions = [];
                Object.keys(fetchedData.progressions)
                    .sort()
                    .forEach(function (key) {
                        fetchedData.progressions[key]["id"] = key;
                        orderedProgressions[key] =
                            fetchedData.progressions[key];
                    });
                // orderedProgressions by date
                let orderedProgressionsDate = orderedProgressions
                    .filter((date) => date["last"] !== null)
                    .sort(
                        (a, b) =>
                            new Date(a["last"]["date"]) -
                            new Date(b["last"]["date"]),
                    )
                    .reverse();
                orderedProgressions = [
                    ...orderedProgressionsDate,
                    ...orderedProgressions.filter(
                        (progression) => progression["last"] === null,
                    ),
                ];
                let allCoursesOrdered = [];
                orderedProgressions.forEach((progression) => {
                    allCourses.forEach((course) => {
                        if (course.product) {
                            if (course.product.id == progression.id) {
                                course.progression = progression;
                                allCoursesOrdered.push(course);
                            }
                        } else if (course.program) {
                            if (course.program.id == progression.id) {
                                course.progression = progression;
                                allCoursesOrdered.push(course);
                            }
                        }
                    });
                });
                setAllCourses(allCoursesOrdered);
                if (searchParams.get("firstTimeMentor")) {
                    router.push("/mentor");
                    return;
                }
                setIsLoadingDashboard(false);
            }
        }
    };

    const loadBadgeData = async () => {
        try {
            const userTimezone = session?.user?.timezone || moment.tz.guess();
            const currentMonth = moment().tz(userTimezone).month() + 1;
            const currentYear = moment().tz(userTimezone).year();

            // Fetch current month badge
            const badgeResponse = await fetch("/api/badges", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    month: currentMonth,
                    year: currentYear,
                }),
            });

            let badgeDataResponse = null;
            if (badgeResponse.ok) {
                badgeDataResponse = await badgeResponse.json();
                setBadgeData(badgeDataResponse);
            }

            // Fetch user progress for current month
            const progressResponse = await fetch("/api/badges/user-progress", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    month: currentMonth,
                    year: currentYear,
                }),
            });

            if (progressResponse.ok) {
                const progressData = await progressResponse.json();
                // Si progressList existe et contient des données, utiliser le premier élément
                // Sinon, créer un objet par défaut avec questCompleted: 0
                const userProgressData =
                    progressData.progressList?.length > 0
                        ? progressData.progressList[0]
                        : { questCompleted: 0, questTarget: null };

                setUserProgress(userProgressData);

                // Animation de la barre de progression
                if (userProgressData && badgeDataResponse?.badges?.[0]) {
                    const targetWidth = Math.min(
                        (userProgressData.questCompleted /
                            (userProgressData.questTarget ||
                                badgeDataResponse.badges[0].questTarget)) *
                            100,
                        100,
                    );
                    setTimeout(() => {
                        setProgressBarWidth(targetWidth);
                    }, 300);
                }
            }
        } catch (error) {
            console.error(
                "Erreur lors du chargement des données de badge:",
                error,
            );
        }
    };

    const loadBebotCover = async () => {
        try {
            const response = await fetch("/api/cover/active", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
            });
            const data = await response.json();
            if (data?.cover?.imageUrl) {
                setBebotCover(data.cover);
            }
        } catch (error) {
            // Silencieux - la couverture est optionnelle
        } finally {
            setIsBebotLoading(false);
        }
    };

    const loadUserAssignments = async () => {
        try {
            const response = await fetch("/api/user/assignments", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response.ok) {
                const data = await response.json();
                setUserAssignments(data.assignments || []);
            }
        } catch (error) {
            console.error("Erreur lors du chargement des assignements:", error);
        }
    };

    useEffect(() => {
        if (!session) return;

        // If the SSR bootstrap already pre-populated the dashboard, we skip
        // the cascade of client fetches entirely. We only refetch the parts
        // that failed (reported in bootstrap.errors).
        const errors = bootstrap?.errors || {};
        const hasBootstrap = bootstrap && bootstrap.personal;

        if (!hasBootstrap || errors.personal) {
            loadDashboard();
        }
        if (!hasBootstrap || errors.badges) {
            loadBadgeData();
        }
        if (!hasBootstrap || errors.assignments) {
            loadUserAssignments();
        }
        if (!hasBootstrap || errors.discordCover) {
            loadBebotCover();
        }

        // If badges came in via bootstrap, animate the progress bar to its target
        if (hasBootstrap && !errors.badges && userProgress && badgeData?.badges?.[0]) {
            const target = Math.min(
                (userProgress.questCompleted /
                    (userProgress.questTarget ||
                        badgeData.badges[0].questTarget)) *
                    100,
                100,
            );
            const animation = setTimeout(() => {
                setProgressBarWidth(target);
            }, 300);
            const videoTimer = setTimeout(() => {
                setRefreshVideo(true);
            }, 3000);
            return () => {
                clearTimeout(animation);
                clearTimeout(videoTimer);
            };
        }

        const timer = setTimeout(() => {
            setRefreshVideo(true);
        }, 3000);
        return () => clearTimeout(timer);
    }, [session]);

    // Update time every minute, synchronized with the start of each minute
    useEffect(() => {
        const now = moment();
        const secondsUntilNextMinute = 60 - now.seconds();
        let timeInterval;

        // First timeout to sync with the next minute
        const initialTimeout = setTimeout(() => {
            setCurrentTime(moment());

            // Then update every minute
            timeInterval = setInterval(() => {
                setCurrentTime(moment());
            }, 60000);
        }, secondsUntilNextMinute * 1000);

        return () => {
            clearTimeout(initialTimeout);
            if (timeInterval) clearInterval(timeInterval);
        };
    }, []);

    useEffect(() => {
        if (!modalIsOpen && modal) {
            window.history.replaceState(
                null,
                "",
                `${process.env.NEXTAUTH_URL}/${locale}`,
            );
        }
    }, [modalIsOpen]);

    if (status === "loading") {
        return (
            <div className="flex flex-col justify-center items-center h-screen">
                <div className="mb-4">
                    <PublicLottieAnimation
                        animation={bebot}
                        width="200px"
                        className="mb-2"
                    />
                </div>
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 dark:border-white"></div>
            </div>
        );
    }

    return (
        <>
            <div className="max-w-7xl mx-auto mb-10 lg:px-5 px-4">
                <div className="flex flex-col xl:flex-row mt-6 lg:mt-10 gap-6 xl:gap-10">
                    <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5 pb-2">
                            <div className="flex flex-1 items-center gap-5 min-w-0">
                                <div className="lg:w-16 lg:h-16 w-14 h-14 rounded-full overflow-hidden shrink-0">
                                    <Image
                                        src={session.user.photo}
                                        alt="User"
                                        width={100}
                                        height={100}
                                        className="w-full h-full rounded-full object-cover"
                                    />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-grayDark-400 mb-1">
                                        {t(
                                            (() => {
                                                const hour = currentTime
                                                    .tz(session.user.timezone)
                                                    .hour();
                                                if (hour >= 5 && hour < 12)
                                                    return "greeting_morning";
                                                if (hour >= 12 && hour < 18)
                                                    return "greeting_afternoon";
                                                return "greeting_evening";
                                            })(),
                                        )}
                                    </p>
                                    <h1 className="lg:text-3xl text-2xl font-bold font-sourceSans text-gray-900 dark:text-white leading-tight">
                                        {session.user.firstName} !
                                    </h1>
                                    <p className="text-sm text-gray-600 dark:text-grayDark-300 mt-1 mb-0 font-medium">
                                        <span className="text-gray-800 dark:text-grayDark-200 font-semibold">
                                            {(() => {
                                                const dateFormats = {
                                                    fr: "dddd DD MMMM, HH:mm",
                                                    en: "dddd, MMMM DD, HH:mm",
                                                };
                                                const format =
                                                    dateFormats[locale] ||
                                                    dateFormats.en;
                                                return currentTime
                                                    .tz(session.user.timezone)
                                                    .locale(locale)
                                                    .format(format)
                                                    .replace(/^\w/, (c) =>
                                                        c.toUpperCase(),
                                                    );
                                            })()}
                                        </span>{" "}
                                        <span className="text-gray-500 dark:text-grayDark-400">
                                            -{" "}
                                            {(() => {
                                                const today = moment()
                                                    .tz(session.user.timezone)
                                                    .format("YYYY-MM-DD");
                                                const hasProgressedToday =
                                                    session.user.activity &&
                                                    session.user.activity[
                                                        today
                                                    ] > 0;

                                                return hasProgressedToday
                                                    ? t("excellent_work_today")
                                                    : t(
                                                          "ready_to_advance_today",
                                                      );
                                            })()}
                                        </span>
                                    </p>
                                </div>
                            </div>

                            {/* Objectif du jour (compact, aligné au bloc de bienvenue) */}
                            <DailyGoalWidget dailyGoal={initialPersonal?.dailyGoal} />
                        </div>

                        {/* Vos formations : la bibliothèque de l'utilisateur,
                            en premier (c'est ce pour quoi on revient). Toujours
                            affichée en entier. */}
                        <CourseSection
                            allCourses={allCourses}
                            isLoading={isLoadingDashboard}
                            onArchiveChange={loadDashboard}
                            archivedCount={archivedCount}
                        />

                        {/* Votre prochaine étape : conseil, juste après la
                            bibliothèque. Le cours en cours peut aussi y figurer,
                            c'est assumé (conseil vs bibliothèque). */}
                        {recommendation && (
                            <RecommendedPath recommendation={recommendation} />
                        )}

                        {/* Assignements des teams */}
                        <AssignmentSection
                            assignments={userAssignments}
                            userTimezone={session.user.timezone}
                        />

                        {/* Events */}
                        {allEventsPlanned.length > 0 && (
                            <div className="mt-14 lg:mt-24">
                                {/* Section Header */}
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-3">
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                                            {t("upcoming_events")}
                                        </h2>
                                        <p className="text-sm text-gray-500 dark:text-grayDark-400">
                                            {t("upcoming_events_subtitle")}
                                        </p>
                                    </div>
                                    <div className="text-sm text-primary font-medium bg-primary/10 px-3 py-1 rounded-full w-fit">
                                        {showAllEvents
                                            ? allEventsPlanned.length
                                            : Math.min(
                                                  allEventsPlanned.length,
                                                  2,
                                              )}{" "}
                                        {t("events_planned", {
                                            count: showAllEvents
                                                ? allEventsPlanned.length
                                                : Math.min(
                                                      allEventsPlanned.length,
                                                      2,
                                                  ),
                                        })}
                                        {!showAllEvents &&
                                            allEventsPlanned.length > 2 && (
                                                <span className="text-gray-500 ml-1">
                                                    / {allEventsPlanned.length}
                                                </span>
                                            )}
                                    </div>
                                </div>

                                {/* Events Grid */}
                                <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                                    {(showAllEvents
                                        ? allEventsPlanned
                                        : allEventsPlanned.slice(0, 2)
                                    ).map((event, index) => (
                                        <div key={`event-${event.id}`}>
                                            <BookedEvent
                                                event={event}
                                                session={session}
                                                index={index}
                                            />
                                        </div>
                                    ))}
                                </div>

                                {/* Show more/less events button */}
                                {allEventsPlanned.length > 2 && (
                                    <div className="mt-4 text-center">
                                        <button
                                            onClick={() =>
                                                setShowAllEvents(!showAllEvents)
                                            }
                                            className="text-primary text-sm font-medium px-4 py-2 rounded-lg hover:bg-primary/5 transition-colors duration-200 flex items-center gap-2 mx-auto"
                                        >
                                            {showAllEvents ? (
                                                <>
                                                    <span>
                                                        {t("show_less_events")}
                                                    </span>
                                                    <svg
                                                        className="w-4 h-4 transform transition-transform duration-200"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M5 15l7-7 7 7"
                                                        />
                                                    </svg>
                                                </>
                                            ) : (
                                                <>
                                                    <span>
                                                        {t("view_all_events", {
                                                            count:
                                                                allEventsPlanned.length -
                                                                2,
                                                        })}
                                                    </span>
                                                    <svg
                                                        className="w-4 h-4 transform transition-transform duration-200"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M19 9l-7 7-7-7"
                                                        />
                                                    </svg>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Statistiques de visionnage : rétrospectif, donc
                            placé plus bas. Le composant se masque lui-même
                            quand il n'y a pas de données (cold-start). */}
                        <WatchStatsSection />

                        {/* Recommandations backend (fallback) : uniquement si
                            le moteur côté catalogue n'a rien produit */}
                        {!recommendation &&
                            recommendedCourses &&
                            recommendedCourses.length > 0 && (
                            <div className="mt-14 lg:mt-24">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                            {t("recommended_courses")}
                                        </h2>
                                        <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                                            {t("recommended_subtitle")}
                                        </p>
                                    </div>
                                    <Link
                                        href="/courses"
                                        className="text-primary text-sm font-medium lg:block hidden"
                                    >
                                        {t("view_all_courses")}
                                    </Link>
                                </div>

                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                                    {recommendedCourses.map((course, index) => {
                                        // Même rendu que /courses via la carte
                                        // partagée : la fiche catalogue (badges,
                                        // durée marketing, niveau) prime sur le
                                        // payload backend, résolue par slug.
                                        const catalogCourse = getCourseBySlug(
                                            course.slug,
                                        );
                                        const displayCourse = catalogCourse || {
                                            slug: course.slug,
                                            title: course.name,
                                            description: course.subtitle,
                                            image: course.cover,
                                            imageIsCover: true,
                                            level: course.level,
                                            duration: course.duration,
                                        };

                                        return (
                                            <CourseCard
                                                key={`recommendation-${course.id || index}`}
                                                course={displayCourse}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Last articles */}
                        {articles && articles.length > 0 && (
                            <div className="mt-14 lg:mt-24">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                            {t("last_articles")}
                                        </h2>
                                        <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                                            {t("last_articles_subtitle")}
                                        </p>
                                    </div>
                                    <Link
                                        href="/articles"
                                        className="text-primary text-sm font-medium lg:block hidden"
                                    >
                                        {t("view_all_articles")}
                                    </Link>
                                </div>

                                <div
                                    className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5"
                                    onClickCapture={handleArticleClickCapture}
                                >
                                    {articles
                                        .slice(0, 6)
                                        .map((article, index, shown) => (
                                            <Link
                                                href={`/r/${article.slug}`}
                                                key={`article-${
                                                    article.id || index
                                                }`}
                                                className={`group h-full ${orphanCardClass(
                                                    index,
                                                    shown.length,
                                                )}`}
                                            >
                                                <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-borderDark bg-white dark:bg-bgDark-840 transition-all duration-200 group-hover:-translate-y-0.5 group-hover:border-gray-300 dark:group-hover:border-bgDark-550 group-hover:shadow-md">
                                                    <div className="relative aspect-[16/9] w-full overflow-hidden bg-gray-100 dark:bg-white/10">
                                                        {/* Sa vignette, ou
                                                            celle qu'on dessine
                                                            a sa place :
                                                            l'initiale sur
                                                            degrade ne disait
                                                            rien de l'article. */}
                                                        <Image
                                                            src={articleCoverPath(
                                                                article,
                                                                locale,
                                                            )}
                                                            alt={article.title}
                                                            fill
                                                            unoptimized={
                                                                !article.cover
                                                            }
                                                            className="object-cover group-hover:scale-105 transition-transform duration-300"
                                                        />
                                                    </div>
                                                    <div className="flex flex-1 flex-col p-4">
                                                        <h3 className="font-semibold text-gray-900 dark:text-white text-sm leading-snug mb-1.5 line-clamp-2">
                                                            {article.title}
                                                        </h3>
                                                        <p className="text-xs text-gray-500 dark:text-grayDark-400 line-clamp-2 mb-3">
                                                            {article.description}
                                                        </p>
                                                        <span className="mt-auto text-xs text-gray-400">
                                                            {moment(
                                                                article.dateCreation,
                                                            )
                                                                .tz(
                                                                    session.user.timezone,
                                                                )
                                                                .locale(locale)
                                                                .fromNow()
                                                                .replace(
                                                                    /^\w/,
                                                                    (c) =>
                                                                        c.toUpperCase(),
                                                                )}
                                                        </span>
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                </div>

                                {/* Show all articles button on mobile */}
                                <div className="mt-4 text-center lg:hidden">
                                    <Link
                                        href="/articles"
                                        className="text-primary text-sm font-medium px-4 py-2 rounded-lg hover:bg-primary/5 transition-colors duration-200 flex items-center gap-2 mx-auto w-fit"
                                    >
                                        <span>{t("view_all_articles")}</span>
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 5l7 7-7 7"
                                            />
                                        </svg>
                                    </Link>
                                </div>
                            </div>
                        )}

                        {/* Most read this week (social proof) */}
                        {popularArticles && popularArticles.length >= 3 && (
                            <div className="mt-14 lg:mt-24">
                                <div className="mb-3">
                                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                        {t("popular_articles")}
                                    </h2>
                                    <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                                        {t("popular_articles_subtitle")}
                                    </p>
                                </div>

                                <div
                                    className="grid grid-cols-1 md:grid-cols-2 gap-3"
                                    onClickCapture={
                                        handlePopularArticleClickCapture
                                    }
                                >
                                    {popularArticles
                                        .slice(0, 4)
                                        .map((article, index) => (
                                            <Link
                                                href={`/r/${article.slug}`}
                                                key={`popular-article-${
                                                    article.id || index
                                                }`}
                                                className="group flex items-center gap-3 rounded-xl border border-gray-200 dark:border-borderDark bg-white dark:bg-bgDark-840 p-3 transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 dark:hover:border-bgDark-550 hover:shadow-md"
                                            >
                                                {/* La place réservée à une
                                                    sortie récente n'a pas été
                                                    gagnée au volume : elle est
                                                    nommée, pas numérotée. */}
                                                <span
                                                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                                                        article.fresh
                                                            ? "bg-primary/10 dark:bg-primary/20 text-primary"
                                                            : "bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-grayDark-300"
                                                    }`}
                                                >
                                                    {article.fresh ? (
                                                        <SolarIcon
                                                            name="stars-linear"
                                                            className="text-sm"
                                                        />
                                                    ) : (
                                                        index + 1
                                                    )}
                                                </span>
                                                <div className="relative h-[3.375rem] w-24 shrink-0 overflow-hidden rounded-lg bg-gray-100 dark:bg-white/10">
                                                    <Image
                                                        src={articleCoverPath(
                                                            article,
                                                            locale,
                                                        )}
                                                        alt={article.title}
                                                        fill
                                                        unoptimized={
                                                            !article.cover
                                                        }
                                                        className="object-cover"
                                                    />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <h3 className="text-sm font-semibold leading-snug text-gray-900 dark:text-white line-clamp-2 group-hover:text-primary transition-colors duration-200">
                                                        {article.title}
                                                    </h3>
                                                    {/* Le classement garde les
                                                        articles déjà ouverts :
                                                        on le dit plutôt que de
                                                        les retirer. */}
                                                    {(article.fresh ||
                                                        article.read) && (
                                                        <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                                                            {article.fresh && (
                                                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
                                                                    <SolarIcon
                                                                        name="stars-linear"
                                                                        className="text-sm"
                                                                    />
                                                                    {t(
                                                                        "popular_articles_fresh",
                                                                    )}
                                                                </span>
                                                            )}
                                                            {article.read && (
                                                                <span className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-grayDark-500">
                                                                    <SolarIcon
                                                                        name="eye-linear"
                                                                        className="text-sm"
                                                                    />
                                                                    {t(
                                                                        "popular_articles_already_read",
                                                                    )}
                                                                </span>
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            </Link>
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="w-full xl:w-[344px]">
                        {/* Current badge for this month */}
                        {badgeData === null ? (
                            <div className="rounded-2xl overflow-hidden">
                                {isBebotLoading ? (
                                    <Skeleton height={180} borderRadius={0} />
                                ) : bebotCover ? (
                                    <Link href="/bebot" className="block">
                                        <div className="relative">
                                            <img
                                                src={bebotCover.imageUrl}
                                                alt={bebotCover.name || "BeBot du jour"}
                                                className="w-full h-auto block"
                                                loading="eager"
                                            />
                                            <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-blue-500 to-transparent" />
                                        </div>
                                    </Link>
                                ) : null}
                                <div className="bg-blue-500 text-white p-4">
                                    <Skeleton width={80} height={28} borderRadius={8} className="mb-3" />
                                    <Skeleton width={180} height={24} className="mb-3" />
                                    <Skeleton width={100} height={16} className="mb-4" />
                                    <div className="bg-white rounded-xl p-3">
                                        <Skeleton width={100} height={16} className="mb-2" />
                                        <div className="flex items-center gap-3">
                                            <Skeleton containerClassName="flex-1" height={20} borderRadius={9999} />
                                            <Skeleton width={60} height={60} circle />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                        <div className="rounded-2xl overflow-hidden">
                            {bebotCover && (
                                <Link href="/bebot" className="block">
                                    <div className="relative">
                                        <img
                                            src={bebotCover.imageUrl}
                                            alt={bebotCover.name || "BeBot du jour"}
                                            className="w-full h-auto block"
                                            loading="eager"
                                        />
                                        <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-blue-500 to-transparent" />
                                    </div>
                                </Link>
                            )}
                        <div className="bg-blue-500 text-white p-4">
                            {/* Month */}
                            <div className="inline-block bg-white text-blue-500 px-3 py-1 rounded-lg font-semibold uppercase mb-3">
                                {(() => {
                                    // Obtenir la timezone de l'utilisateur ou deviner
                                    const userTimezone =
                                        session?.user?.timezone ||
                                        moment.tz.guess();
                                    return moment()
                                        .locale(locale)
                                        .tz(userTimezone)
                                        .format("MMMM");
                                })()}
                            </div>

                            {/* Titre */}
                            <div className="mb-3">
                                <h2 className="text-xl font-bold text-white">
                                    {(() => {
                                        if (
                                            !badgeData?.badges?.[0]
                                                ?.nameTranslations
                                        )
                                            return "BeBot dans les nuages";

                                        const nameTranslations =
                                            badgeData.badges[0]
                                                .nameTranslations;

                                        // Récupérer le nom selon la locale actuelle
                                        return (
                                            nameTranslations[locale] ||
                                            nameTranslations.en ||
                                            nameTranslations.fr ||
                                            "BeBot dans les nuages"
                                        );
                                    })()}
                                </h2>
                            </div>

                            {/* Temps restant avec icône horloge */}
                            <div className="flex items-center text-blue-300 gap-2 mb-4">
                                <svg
                                    className="w-4 h-4"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                <span className="text-sm uppercase">
                                    {(() => {
                                        // Calculer le temps restant jusqu'à la fin du mois
                                        const userTimezone =
                                            session?.user?.timezone ||
                                            moment.tz.guess();
                                        const now = moment().tz(userTimezone);
                                        const endOfMonth = moment()
                                            .tz(userTimezone)
                                            .endOf("month");
                                        const timeLeft = moment.duration(
                                            endOfMonth.diff(now),
                                        );

                                        const daysLeft = Math.floor(
                                            timeLeft.asDays(),
                                        );
                                        const hoursLeft = Math.floor(
                                            timeLeft.asHours(),
                                        );
                                        const minutesLeft = Math.floor(
                                            timeLeft.minutes(),
                                        );

                                        if (daysLeft >= 1) {
                                            return `${daysLeft} ${
                                                daysLeft > 1
                                                    ? t("days")
                                                    : t("day")
                                            }`;
                                        } else if (hoursLeft >= 1) {
                                            return `${hoursLeft} ${
                                                hoursLeft > 1
                                                    ? t("hours")
                                                    : t("hour")
                                            }`;
                                        } else {
                                            return `${minutesLeft} ${
                                                minutesLeft > 1
                                                    ? t("minutes")
                                                    : t("minute")
                                            }`;
                                        }
                                    })()}
                                </span>
                            </div>

                            {/* Card */}
                            <div className="bg-white rounded-xl p-3 relative overflow-hidden">
                                {userProgress &&
                                    badgeData?.badges?.[0] &&
                                    userProgress.questCompleted >=
                                        (userProgress.questTarget ||
                                            badgeData.badges[0]
                                                .questTarget) && (
                                        <div
                                            className="absolute inset-0 pointer-events-none opacity-[0.06]"
                                            style={{
                                                backgroundImage:
                                                    "repeating-linear-gradient(45deg, #10b981, #10b981 1px, transparent 1px, transparent 8px)",
                                            }}
                                        />
                                    )}
                                <div className="text-gray-800 font-bold mb-2 relative">
                                    {userProgress &&
                                    badgeData?.badges?.[0] &&
                                    userProgress.questCompleted >=
                                        (userProgress.questTarget ||
                                            badgeData.badges[0].questTarget)
                                        ? "Défi terminé !"
                                        : "Défi en cours"}
                                </div>

                                {/* Progress et Badge sur la même ligne */}
                                <div className="flex items-center gap-3">
                                    <div className="flex-1 bg-blue-200 h-5 rounded-full relative overflow-hidden">
                                        {/* Barre de progression avec animation */}
                                        <div
                                            className="bg-blue-600 h-5 rounded-full relative transition-all duration-1000 ease-out"
                                            style={{
                                                width: `${progressBarWidth}%`,
                                            }}
                                        >
                                            {/* Ligne au milieu */}
                                            <div
                                                className="absolute inset-0 bg-white opacity-20 rounded-full"
                                                style={{
                                                    height: "30%",
                                                    top: "25%",
                                                    left: "1px",
                                                    width: "calc(100% - 2px)",
                                                    transition: "width 0.4s",
                                                }}
                                            ></div>
                                        </div>
                                        {/* Texte superposé toujours visible */}
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-white text-xs font-semibold relative z-10">
                                                {userProgress &&
                                                badgeData?.badges?.[0]
                                                    ? `${
                                                          userProgress.questCompleted ||
                                                          0
                                                      } / ${
                                                          userProgress.questTarget ||
                                                          badgeData.badges[0]
                                                              .questTarget
                                                      }`
                                                    : badgeData?.badges?.[0]
                                                    ? `0 / ${badgeData.badges[0].questTarget}`
                                                    : "0 / 0"}
                                            </span>
                                        </div>
                                    </div>

                                    <div
                                        className={`w-[60px] h-[60px] rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer transition-all duration-300 relative ${
                                            userProgress &&
                                            badgeData?.badges?.[0] &&
                                            userProgress.questCompleted >=
                                                (userProgress.questTarget ||
                                                    badgeData.badges[0]
                                                        .questTarget)
                                                ? "bg-white hover:scale-110"
                                                : "bg-white hover:scale-105"
                                        }`}
                                        onClick={() => {
                                            if (badgeData && userProgress) {
                                                const userTimezone =
                                                    session?.user?.timezone ||
                                                    moment.tz.guess();
                                                const currentMonth = moment()
                                                    .tz(userTimezone)
                                                    .locale(locale)
                                                    .format("MMMM");
                                                const isUnlocked =
                                                    userProgress.questCompleted >=
                                                    (userProgress.questTarget ||
                                                        badgeData.badges[0]
                                                            .questTarget);
                                                // Récupérer le nom localisé du badge
                                                const getLocalizedBadgeName =
                                                    () => {
                                                        if (
                                                            !badgeData
                                                                ?.badges?.[0]
                                                                ?.nameTranslations
                                                        )
                                                            return `Défi du mois de ${currentMonth}`;

                                                        const nameTranslations =
                                                            badgeData.badges[0]
                                                                .nameTranslations;

                                                        return (
                                                            nameTranslations[
                                                                locale
                                                            ] ||
                                                            nameTranslations.en ||
                                                            nameTranslations.fr ||
                                                            `Défi du mois de ${currentMonth}`
                                                        );
                                                    };

                                                setModal({
                                                    type: "badge",
                                                    src:
                                                        badgeData?.badges?.[0]
                                                            ?.imageUrl ||
                                                        "/static/badges/default.svg",
                                                    alt: `Badge du mois de ${currentMonth}`,
                                                    isUnlocked: isUnlocked,
                                                    challengeName:
                                                        getLocalizedBadgeName(),
                                                    progress: {
                                                        current:
                                                            userProgress.questCompleted,
                                                        total:
                                                            userProgress.questTarget ||
                                                            badgeData.badges[0]
                                                                .questTarget,
                                                    },
                                                });
                                                setModalIsOpen(true);
                                            }
                                        }}
                                    >
                                        {/* Indicateur de déverrouillage */}
                                        {userProgress &&
                                            badgeData?.badges?.[0] &&
                                            userProgress.questCompleted >=
                                                (userProgress.questTarget ||
                                                    badgeData.badges[0]
                                                        .questTarget) && (
                                                <>
                                                    {/* Étoile en haut à droite */}
                                                    <div className="absolute -top-1 -right-1 text-yellow-400 animate-pulse z-20">
                                                        <svg
                                                            className="w-4 h-4"
                                                            fill="currentColor"
                                                            viewBox="0 0 20 20"
                                                        >
                                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                        </svg>
                                                    </div>
                                                    {/* Halo lumineux */}
                                                    <div className="absolute inset-0 rounded-full animate-pulse bg-gradient-to-r from-yellow-300/20 to-amber-300/20 blur-xl"></div>
                                                    {/* Particules scintillantes */}
                                                    <div className="absolute inset-0">
                                                        <div className="absolute top-0 left-2 w-1 h-1 bg-yellow-300 rounded-full animate-ping"></div>
                                                        <div className="absolute bottom-2 right-0 w-1 h-1 bg-amber-400 rounded-full animate-ping animation-delay-200"></div>
                                                        <div className="absolute top-3 right-3 w-1 h-1 bg-yellow-400 rounded-full animate-ping animation-delay-400"></div>
                                                    </div>
                                                </>
                                            )}

                                        <Image
                                            src={
                                                badgeData?.badges?.[0]
                                                    ?.imageUrl ||
                                                "/static/badges/default.svg"
                                            }
                                            alt={`Badge du mois de ${(() => {
                                                const userTimezone =
                                                    session?.user?.timezone ||
                                                    moment.tz.guess();
                                                return moment()
                                                    .tz(userTimezone)
                                                    .locale(locale)
                                                    .format("MMMM");
                                            })()}`}
                                            width={60}
                                            height={60}
                                            className={`relative z-10 transition-all duration-500 ${
                                                userProgress &&
                                                badgeData?.badges?.[0] &&
                                                userProgress.questCompleted >=
                                                    (userProgress.questTarget ||
                                                        badgeData.badges[0]
                                                            .questTarget)
                                                    ? "drop-shadow-lg"
                                                    : "opacity-75 grayscale"
                                            }`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        </div>
                        )}

                        {/* Reps : leçon du jour (point d'entrée quotidien) */}
                        <RepsWidget />

                        {/* Quests */}
                        <div className="mt-4">
                            <QuestList />
                        </div>

                        {/* Current leagues */}
                        <LeagueWidget />

                        {/* Free trial upsell card : ne s'affiche que si le
                            tunnel accordera vraiment l'essai (interrupteur
                            d'onboarding piloté depuis /admin/promotions) */}
                        {!session.user.pro && !session.user.currentTeam && onboardingTrial.enabled && (
                            <div className="mt-4">
                                <div className="relative overflow-hidden rounded-2xl border border-gray-200/60 bg-white p-5 dark:border-borderDark dark:bg-bgDark-840 backdrop-blur-sm shadow-sm">
                                    <div className="flex items-start gap-3">
                                        <div className="flex-1">
                                            <div className="mb-2 inline-flex items-center rounded-md bg-gradient-to-r from-orange-500 to-pink-500 px-2 py-0.5">
                                                <span className="text-xs font-bold uppercase tracking-wider text-white">Pro</span>
                                            </div>
                                            <h3 className="text-base font-bold text-gray-900 dark:text-white leading-snug">
                                                {t("free_trial_card_title")}
                                            </h3>
                                            <p className="mt-1 text-sm text-gray-500 dark:text-grayDark-400 mb-0 leading-snug">
                                                {t("free_trial_card_description")}
                                            </p>
                                        </div>
                                        <Image
                                            src="/images/bebot/left/HEUREUX.svg"
                                            alt="BeBot"
                                            width={72}
                                            height={72}
                                            className="flex-shrink-0 -mt-1"
                                        />
                                    </div>
                                    <Link href="/pricing">
                                        <button className="btn-3d lip-indigo mt-4 w-full rounded-xl bg-indigo-600 px-4 py-3 text-sm font-bold uppercase tracking-wide text-white shadow-md shadow-indigo-500/25 transition-all hover:shadow-lg hover:shadow-indigo-500/30">
                                            {t("free_trial_card_cta", { days: onboardingTrial.days })}
                                        </button>
                                    </Link>
                                </div>
                            </div>
                        )}

                        {/* Full access with Premium and Teams */}
                        {/* <PlanSwitcher /> */}
                    </div>
                </div>

                {/* Join premium / teams */}
                {!session.user.pro && !session.user.currentTeam && <PremiumCTA />}
            </div>

            {/* Footer */}
            <Footer />

            {/* Modal pour prévisualiser l'image du badge */}
            <BottomSheetModal
                isOpen={modalIsOpen && !!modal}
                onClose={() => {
                    setModalIsOpen(false);
                    setModal(null);
                }}
                maxWidthClass="sm:max-w-md"
                zIndexClass="z-50"
            >
                {modal && (
                    <div className="relative">
                        <div
                            className={`relative overflow-hidden rounded-2xl ${
                                modal.type === "badge" &&
                                modal.isUnlocked
                                    ? "ring-1 ring-amber-200/40 dark:ring-amber-500/20"
                                    : "ring-1 ring-gray-200 dark:ring-grayDark-700/50"
                            }`}
                        >
                            {/* Section haute avec gradient atmosphérique */}
                            <div
                                className={`relative px-6 pt-6 pb-8 ${
                                    modal.type === "badge" &&
                                    modal.isUnlocked
                                        ? "bg-gradient-to-b from-amber-50 via-amber-50/40 to-white dark:from-grayDark-800 dark:via-grayDark-900/95 dark:to-grayDark-900"
                                        : "bg-gradient-to-b from-gray-50 to-white dark:from-grayDark-800 dark:to-grayDark-900"
                                }`}
                            >
                                {/* Bouton fermer */}
                                <button
                                    onClick={() => {
                                        setModalIsOpen(false);
                                        setModal(null);
                                    }}
                                    className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/15 flex items-center justify-center transition-colors"
                                >
                                    <svg
                                        className="w-4 h-4 text-gray-400 dark:text-grayDark-500"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>

                                {/* Label discret */}
                                <p className="text-center text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-400 dark:text-grayDark-500 mb-0">
                                    {modal.type === "badge"
                                        ? "Badge du mois"
                                        : "Prévisualisation"}
                                </p>

                                {modal.type === "badge" && (
                                    <>
                                        {/* Badge image avec halo ambiant */}
                                        <div className="relative mx-auto w-36 h-36 my-6">
                                            {modal.isUnlocked && (
                                                <>
                                                    <div className="absolute inset-0 scale-150 rounded-full bg-amber-300/20 dark:bg-amber-500/15 blur-2xl animate-pulse" />
                                                    <div className="absolute -inset-2.5 rounded-full border border-amber-300/20 dark:border-amber-400/10" />
                                                </>
                                            )}
                                            <Image
                                                src={modal.src}
                                                alt={modal.alt}
                                                width={144}
                                                height={144}
                                                className={`relative rounded-full transition-all duration-500 ${
                                                    modal.isUnlocked
                                                        ? "drop-shadow-[0_8px_32px_rgba(245,158,11,0.25)] hover:scale-105"
                                                        : "grayscale opacity-50 drop-shadow-md"
                                                }`}
                                            />
                                        </div>

                                        {/* Nom du badge */}
                                        <h4 className="text-center text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                                            {modal.challengeName ||
                                                "Badge du mois"}
                                        </h4>
                                    </>
                                )}
                            </div>

                            {/* Section contenu */}
                            {modal.type === "badge" && (
                                <div className="bg-white dark:bg-grayDark-900 px-6 pb-6 text-center">
                                    {/* Pastille de statut */}
                                    {modal.isUnlocked ? (
                                        <div className="inline-flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                                            <svg
                                                className="w-3.5 h-3.5"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                            Badge débloqué
                                        </div>
                                    ) : (
                                        <div className="inline-flex items-center gap-1.5 bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                                            <svg
                                                className="w-3.5 h-3.5"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                            Badge à débloquer
                                        </div>
                                    )}

                                    {/* Description */}
                                    <p className="text-sm text-gray-500 dark:text-grayDark-400 leading-relaxed max-w-sm mx-auto mb-5">
                                        {modal.isUnlocked
                                            ? "Félicitations ! Vous avez terminé le défi et obtenu ce badge pour vos efforts ce mois-ci."
                                            : "Ce badge sera débloqué une fois que vous aurez terminé le défi du mois. Continuez vos efforts !"}
                                    </p>

                                    {/* Barre de progression */}
                                    {modal.progress && (
                                        <div className="bg-gray-50 dark:bg-white/[0.03] rounded-xl p-4 ring-1 ring-gray-100 dark:ring-white/[0.06]">
                                            <div className="flex items-center justify-between mb-2.5">
                                                <span className="text-xs font-medium text-gray-400 dark:text-grayDark-500">
                                                    {modal.isUnlocked
                                                        ? "Progression du défi"
                                                        : "Progression actuelle"}
                                                </span>
                                                <span className="text-sm font-bold text-gray-800 dark:text-grayDark-200 tabular-nums">
                                                    {
                                                        modal.progress
                                                            .current
                                                    }
                                                    <span className="text-gray-300 dark:text-grayDark-600 mx-0.5">
                                                        /
                                                    </span>
                                                    {
                                                        modal.progress
                                                            .total
                                                    }
                                                </span>
                                            </div>
                                            <div className="w-full bg-gray-200/70 dark:bg-grayDark-700/50 rounded-full h-2 overflow-hidden">
                                                <div
                                                    className={`h-2 rounded-full transition-all duration-1000 ease-out ${
                                                        modal.isUnlocked
                                                            ? "bg-gradient-to-r from-emerald-400 to-emerald-500"
                                                            : "bg-gradient-to-r from-orange-400 to-orange-500"
                                                    }`}
                                                    style={{
                                                        width: `${Math.min(
                                                            (modal
                                                                .progress
                                                                .current /
                                                                modal
                                                                    .progress
                                                                    .total) *
                                                                100,
                                                            100
                                                        )}%`,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </BottomSheetModal>
        </>
    );
}
